import { ReactNode } from 'react';

import { InkDialog } from '@movable/ui';
import {
  Box,
  Breakpoint,
  DialogContent,
  DialogTitle,
  PaperProps,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import Loading from '@/components/utils/Loading';

import { ModalButtonType, convertToActionProp } from './utils';

type InkModalProps = {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  title: string | JSX.Element | ReactNode;
  children: ReactNode;
  styles?: SxProps<Theme>;
  dialogContentStyles?: SxProps<Theme>;
  primaryButton?: ModalButtonType;
  secondaryButton?: ModalButtonType;
  tertiaryButton?: ModalButtonType;
  maxWidth?: false | Breakpoint | undefined;
  paperProps?: PaperProps;
  hideCloseButton?: boolean;
  ActionDescription?: string | JSX.Element;
};

const InkModal = ({
  open,
  onClose,
  loading,
  children,
  title,
  styles = {},
  dialogContentStyles = {},
  primaryButton,
  secondaryButton,
  tertiaryButton,
  maxWidth = 'sm',
  paperProps,
  hideCloseButton = false,
  ActionDescription,
}: InkModalProps) => {
  const getTitle = () => {
    if (typeof title === 'string') {
      return title;
    }
    return (
      <DialogTitle data-test="ink-dialog-title">
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            <strong>{title}</strong>
          </Typography>
        </Stack>
      </DialogTitle>
    );
  };

  return (
    <InkDialog
      open={open}
      onClose={onClose}
      Title={getTitle()}
      hideCloseButton={hideCloseButton}
      Content={
        <>
          {loading ? (
            <Box
              sx={{
                minHeight: 200,
              }}
            >
              <Loading />
            </Box>
          ) : (
            <DialogContent sx={dialogContentStyles}>{children}</DialogContent>
          )}
        </>
      }
      ActionDescription={ActionDescription}
      ActionsProp={{
        ...(primaryButton && {
          primary: convertToActionProp(primaryButton),
        }),
        ...(secondaryButton && {
          secondary: convertToActionProp(secondaryButton),
        }),
        ...(tertiaryButton && {
          cancel: convertToActionProp(tertiaryButton),
        }),
      }}
      sx={{ ...styles }}
      fullWidth
      maxWidth={maxWidth}
      slotProps={{ paper: paperProps }}
    />
  );
};

export default InkModal;
